// 各國旅客購物退稅規則。只收官方頁面寫明、且各官方頁面彼此一致的內容；
// 官方說法互相矛盾的項目（如泰國海關查驗金額門檻）不寫數字，只以 note 提醒看現場公告。
// 不寫實際退稅比例（要扣手續費）與參與商店（官方沒有統一資料）。
//
// 規則會改（日本 2026-11-01 就改制）：改動時必須同時更新 `verified`，
// 並由 scripts/check-tax-refund-links.js 定期確認來源頁面還在。
// key 與 country_facts / ESIM_SLUGS 的英文國家名一致。

const T = {
  Japan: {
    verified: '2026-10-19',
    tag: { zh: '2026-11-01 起新制', en: 'New system from 2026-11-01' },
    zh: [
      '2026 年 11 月 1 日起改為退稅制：店內先付含稅全額，出境時經海關確認貨品已帶出日本，才退還消費稅。',
      '同一店家、同一天，未稅 5,000 日圓以上。',
      '購物後 90 天內，在出境的機場或港口辦理，並須在託運行李前完成。',
      '食品、飲料、化妝品等消耗品，若在日本境內使用或食用，不能退稅。',
    ],
    en: [
      'From 1 Nov 2026 tax-free shopping becomes a refund system: you pay the full tax-inclusive price in the store, and the consumption tax is refunded after customs confirms at departure that the goods are leaving Japan.',
      'At least ¥5,000 before tax at one store on one day.',
      'Complete it at your departure airport or seaport within 90 days of purchase, before checking in baggage.',
      'Consumables such as food, drinks and cosmetics do not qualify if they are used or consumed in Japan.',
    ],
    sources: [
      { label: 'JNTO', url: 'https://www.japan.travel/en/us/blog/everything-you-need-to-know-japans-new-tax-free-shopping-system/' },
      { label: { zh: '觀光廳免稅店網站', en: 'Japan Tourism Agency' }, url: 'https://www.mlit.go.jp/kankocho/tax-free/page01_000001_00028.html' },
    ],
  },
  'South Korea': {
    verified: '2026-10-19',
    zh: [
      '單張收據至少 15,000 韓元，須是全新、未開封、未使用的商品。',
      '即時退稅（店內當場扣）：單筆 15,000 至不滿 1,000,000 韓元，整趟旅程累計不超過 5,000,000 韓元。',
      '市區退稅櫃檯：單張收據不超過 6,000,000 韓元。',
      '停留不超過 6 個月的外國人適用，貨品須在 3 個月內帶出韓國。',
    ],
    en: [
      'At least KRW 15,000 per receipt, for new, unopened and unused goods.',
      'Immediate refund at the store: a single payment from KRW 15,000 up to under KRW 1,000,000, with total trip spending not above KRW 5,000,000.',
      'Downtown refund counters: a single receipt must not exceed KRW 6,000,000.',
      'For foreigners staying up to 6 months; goods must leave Korea within 3 months.',
    ],
    note: {
      zh: '部分商店自己的頁面寫的金額範圍不同，以店內告示為準。',
      en: 'Some shops publish different limits on their own pages; follow the notice in the store.',
    },
    sources: [
      { label: 'VISITKOREA', url: 'https://english.visitkorea.or.kr/svc/contents/contentsView.do?vcontsId=140736' },
      { label: { zh: '韓國關稅廳', en: 'Korea Customs Service' }, url: 'https://www.customs.go.kr/english/cm/cntnts/cntntsView.do?mi=10803&cntntsId=5504' },
    ],
  },
  Taiwan: {
    verified: '2026-10-19',
    zh: [
      '同一天、向同一家貼有「外籍旅客購物退稅」標誌的特約商店購物，含稅累計新台幣 2,000 元以上。',
      '購物當日持入境證照向店員申請退稅表單，並在 90 天內隨行帶出境。',
      '須在首次攜帶貨物出境前完成退稅申請，事後無法補辦。',
    ],
    en: [
      'At least NT$2,000 including tax, spent at one participating store (look for the tourist tax refund sign) on one day.',
      'Show your entry travel document on the day of purchase to get the refund form, and take the goods out of Taiwan within 90 days.',
      'The refund application must be completed before you first take the goods out of the country; it cannot be made up afterwards.',
    ],
    sources: [
      { label: { zh: '交通部觀光署', en: 'Taiwan Tourism Administration' }, url: 'https://www.taiwan.net.tw/m1.aspx?sNo=0024566' },
      { label: { zh: '財政部法規', en: 'MOF regulation' }, url: 'https://law-out.mof.gov.tw/LawContent.aspx?id=FL026299' },
    ],
  },
  Thailand: {
    verified: '2026-10-19',
    zh: [
      '向貼有「VAT REFUND FOR TOURISTS」標誌的商店購買，每家店每天含 VAT 滿 2,000 泰銖；購物時出示護照，請店員開立 P.P.10 退稅表。',
      '購買後 60 天內帶出泰國，並在離境當天辦理。',
      '只適用搭飛機離境，陸路出境不能辦理。',
    ],
    en: [
      'Buy from stores displaying the "VAT REFUND FOR TOURISTS" sign: at least 2,000 baht including VAT per store per day. Show your passport and ask the shop to issue a P.P.10 form.',
      'Take the goods out of Thailand within 60 days of purchase and claim on your departure date.',
      'Available only when departing by air; not for land border crossings.',
    ],
    note: {
      zh: '海關查驗的金額門檻，官方各頁面說法不一，請以機場現場公告為準。',
      en: 'Official pages differ on the customs inspection threshold; follow the airport notice.',
    },
    sources: [
      { label: { zh: '泰國稅務局', en: 'Revenue Department' }, url: 'https://vrtweb.rd.go.th/81.html' },
      { label: 'FAQ', url: 'https://www.rd.go.th/63040.html' },
    ],
  },
  Singapore: {
    verified: '2026-10-19',
    zh: [
      '向參與 eTRS 的商店購買，單張發票（含 GST）至少 S$100；同一店（同一 GST 註冊號碼與店名）、同一天最多可累計 3 張發票。',
      '購買當天年滿 16 歲，貨品放在隨身行李，經樟宜或實里達機場離境。',
      '在機場的 eTRS 自助機辦理。',
    ],
    en: [
      'Buy from retailers participating in eTRS: at least S$100 (including GST) per invoice; up to three same-day invoices from the same shop (same GST registration number and shop name) can be combined.',
      'You must be 16 or older on the purchase date, carry the goods in your personal luggage and depart via Changi or Seletar Airport.',
      'Claim at the eTRS self-help kiosks at the airport.',
    ],
    sources: [
      { label: { zh: '新加坡海關署', en: 'Singapore Customs' }, url: 'https://www.customs.gov.sg/at-customs/departing-singapore/tourist-refund-scheme/' },
      { label: 'IRAS', url: 'https://www.iras.gov.sg/taxes/goods-services-tax-(gst)/consumers/tourist-refund-scheme' },
    ],
  },

  // ── 歐洲 ─ 2026-10-19 逐頁核對。歐盟國家的規則是各國自訂，金額與期限不能互相套用。
  France: {
    verified: '2026-10-19',
    zh: [
      '同一家商店（或同一集團的商店）3 天內累計消費須超過 100 歐元。',
      '年滿 16 歲，居住在歐盟以外，在法國或歐盟停留不到 6 個月。',
      '退稅單須在購買當月之後第三個月月底前，取得海關驗證。',
    ],
    en: [
      'Purchases at one shop (or one group of shops) over a maximum of 3 days must total more than €100.',
      'You must be 16 or older, live outside the EU, and stay in France or the EU for less than 6 months.',
      'The refund form must be validated by customs before the end of the third month after the month of purchase.',
    ],
    note: { zh: '商店可以自行決定要不要提供退稅，不是義務。', en: 'Shops are not obliged to offer tax-free sales.' },
    sources: [{ label: { zh: '法國海關', en: 'French Customs' }, url: 'https://www.douane.gouv.fr/demarche/vous-achetez-des-marchandises-en-detaxe' }],
  },
  Italy: {
    verified: '2026-10-19',
    zh: [
      '每張發票金額須超過 70 歐元。',
      '居住或長住地在歐盟以外，貨品供個人或家庭使用，並放在隨身行李。',
      '貨品須在發票開立日期之後的第三個月內離開歐盟，並取得海關驗證（visto doganale）。',
    ],
    en: [
      'The value of the goods on each invoice must be more than €70.',
      'You must live outside the EU, the goods must be for personal or family use and carried in your personal luggage.',
      'The goods must leave the EU by the third month after the invoice date, proven by the customs stamp (visto doganale).',
    ],
    sources: [{ label: { zh: '義大利海關專賣署', en: 'Italian Customs and Monopolies Agency' }, url: 'https://www.adm.gov.it/portale/en/rimborso-iva' }],
  },
  Germany: {
    verified: '2026-10-19',
    zh: [
      '單張收據含稅金額須超過 50 歐元（即 50.01 歐元起）。',
      '住所在歐盟以外，並能用護照、身分證等證件向店員證明。國籍不是條件，只看居住地。',
    ],
    en: [
      'The amount on a single receipt including VAT must exceed €50 (from €50.01).',
      'You must live outside the EU and be able to prove it to the shop with a passport, ID or other document. Nationality does not matter, only residence.',
    ],
    sources: [{ label: { zh: '德國海關', en: 'German Customs' }, url: 'https://www.zoll.de/DE/Privatpersonen/Reisen/Reisen-nach-Deutschland-aus-einem-nicht-eu-Staat/Zoll-und-Steuern/Tax-free-einkaufen/tax-free-einkaufen_node.html' }],
  },
  Switzerland: {
    verified: '2026-10-19',
    zh: [
      '零售價格須至少 300 瑞士法郎（含 VAT）。',
      '買家的住所須在瑞士境外。',
    ],
    en: [
      'The retail price must be at least CHF 300 including VAT.',
      'The buyer must not be domiciled in Switzerland.',
    ],
    sources: [{ label: { zh: '瑞士聯邦稅務局', en: 'Swiss Federal Tax Administration' }, url: 'https://www.estv.admin.ch/en/tax-free-for-tourists' }],
  },
  Austria: {
    verified: '2026-10-19',
    zh: [
      '發票金額（可含 VAT）須超過 75 歐元。',
      '護照或其他出入境證件上登記的居住地在歐盟以外。',
      '貨品須放在隨身行李，在購買當月之後第三個曆月結束前運出歐盟。',
    ],
    en: [
      'The invoiced amount (possibly including VAT) must exceed €75.',
      'Your passport or other cross-border travel document must show a domicile outside the EU.',
      'Export the goods in your personal luggage before the end of the third calendar month after the month of purchase.',
    ],
    sources: [{ label: { zh: '奧地利財政部', en: 'Austrian Federal Ministry of Finance' }, url: 'https://www.bmf.gv.at/en/topics/customs/travellers/vat-refund.html' }],
  },
  Denmark: {
    verified: '2026-10-19',
    zh: [
      '貨品價值須超過 300 丹麥克朗。',
      '貨品須在購買當月之後第三個月月底前運出歐盟。',
    ],
    en: [
      'The value of the goods must be more than DKK 300.',
      'The goods must leave the EU before the end of the third month after the month of purchase.',
    ],
    sources: [{ label: { zh: '丹麥海關', en: 'Danish Customs Agency' }, url: 'https://toldst.dk/en-us/individuals/vat-refunds' }],
  },
  Finland: {
    verified: '2026-10-19',
    zh: [
      '同一張收據上的貨品合計至少 40 歐元（含 VAT）。',
      '貨品須未使用，放在隨身行李帶出，並在購買後 3 個月內運出歐盟。',
    ],
    en: [
      'The total value of the goods on one receipt must be at least €40 including VAT.',
      'The goods must be unused, carried in your personal luggage and exported from the EU within three months.',
    ],
    sources: [{ label: { zh: '芬蘭稅務局', en: 'Finnish Tax Administration' }, url: 'https://www.vero.fi/en/detailed-guidance/guidance/48464/tax-free-sales-to-travellers-in--finland/' }],
  },
  Hungary: {
    verified: '2026-10-19',
    zh: [
      '購買金額須超過 175 歐元（2026 年相當於 68,000 福林）。',
      '貨品須全新未使用，並在 90 天內運出歐盟。',
    ],
    en: [
      'The purchase amount must exceed €175 (68,000 forints in 2026).',
      'The goods must be new and unused and leave the EU within 90 days.',
    ],
    sources: [{ label: { zh: '匈牙利稅務海關署', en: 'Hungarian Tax and Customs Authority' }, url: 'https://nav.gov.hu/en/intormation_on_customs_matters/travel-guides---faq/english/vat-refunds-for-foreign-travellers' }],
  },
  Iceland: {
    verified: '2026-10-19',
    zh: [
      '單張收據至少 12,000 冰島克朗。',
      '收據正本須附在退稅單上，並有店員簽名。',
      '貨品須在購買後 3 個月內運出冰島，退稅單須在機場完成出口驗證（蓋章）。',
    ],
    en: [
      'The minimum on a single purchase receipt is ISK 12,000.',
      'The original receipt must be attached to the tax free form, with the retailer\'s signature.',
      'The goods must leave Iceland within three months, and the form must be export validated (stamped) at the airport.',
    ],
    sources: [
      { label: { zh: '冰島稅務海關署', en: 'Iceland Revenue and Customs' }, url: 'https://www.skatturinn.is/english/individuals/customs-matters/travelling-to-iceland/tax-free-vat-refund/' },
      { label: 'island.is', url: 'https://island.is/en/tax-free-shopping-in-iceland' },
    ],
  },
  Norway: {
    verified: '2026-10-19',
    zh: [
      '居住地不在瑞典、丹麥、芬蘭的旅客：單張發票須至少 250 挪威克朗（不含 VAT）。',
      '從拿到貨品到帶出挪威，不得超過 1 個月。',
    ],
    en: [
      'If you live outside Sweden, Denmark and Finland, the individual invoice amount must be at least NOK 250 excluding VAT.',
      'No more than one month may pass between receiving the goods and leaving Norway with them.',
    ],
    note: { zh: '退稅公司（如 Global Blue）可能有自己的最低消費，以店內告示為準。', en: 'Refund operators such as Global Blue may set their own minimum; follow the notice in the shop.' },
    sources: [{ label: { zh: '挪威海關', en: 'Norwegian Customs' }, url: 'https://www.toll.no/en/travelling-to-and-from-norway/reimbursement-of-vat-to-tourists' }],
  },
  Portugal: {
    verified: '2026-10-19',
    zh: [
      '購買總額須超過 50 歐元（不含 VAT）。',
      '居住在歐盟以外，購物時向店員出示護照或能證明居住地的證件。',
      '貨品供個人使用並放在隨身行李，購買後 3 個月內離開歐盟。',
    ],
    en: [
      'Your total purchase must exceed €50 excluding VAT.',
      'You must live outside the EU and show your passport or an ID proving your residence in the shop.',
      'The goods must be for personal use, carried in your personal luggage, and leave the EU within three months.',
    ],
    sources: [{ label: { zh: '葡萄牙稅務局', en: 'Portuguese Tax Authority' }, url: 'https://info.portaldasfinancas.gov.pt/en/tax-information/travellers-and-customs/e-taxfree-vat-refund-for-tourists/Pages/default.aspx' }],
  },
  Spain: {
    verified: '2026-10-19',
    zh: [
      '住在歐盟以外的人適用（另包括加那利群島、休達、梅利利亞居民），只限個人使用的購買。',
      '須在購買後 3 個月內帶著貨品離開歐盟，並在出境前完成驗證，逾期不能退稅。',
    ],
    en: [
      'For people living outside the EU (also residents of the Canary Islands, Ceuta and Melilla), for personal-use purchases only.',
      'You must leave the EU with the goods within 3 months of purchase and have them validated before leaving; otherwise no refund.',
    ],
    note: { zh: '最低消費金額官方資料說法不一，請以店內告示為準。', en: 'Official sources differ on the minimum purchase; follow the notice in the shop.' },
    sources: [{ label: { zh: '西班牙稅務局', en: 'Spanish Tax Agency' }, url: 'https://sede.agenciatributaria.gob.es/Sede/viajeros-trabajadores-desplazados-fronterizos/devoluciones-iva-compras-viajeros/informacion-general-sobre-devolucion-iva-viajeros.html' }],
  },
  'United Kingdom': {
    verified: '2026-10-19',
    tag: { zh: '英國本島已取消', en: 'Ended in Great Britain' },
    zh: [
      '英格蘭、蘇格蘭、威爾斯自 2021 年 1 月 1 日起，已取消旅客購物退稅（VAT Retail Export Scheme）。',
      '北愛爾蘭的商店仍可對非歐盟旅客提供此計畫。',
    ],
    en: [
      'The VAT Retail Export Scheme for visitors was withdrawn in Great Britain (England, Scotland and Wales) from 1 January 2021.',
      'Retailers in Northern Ireland can still offer the scheme to non-EU visitors.',
    ],
    sources: [{ label: 'GOV.UK', url: 'https://www.gov.uk/government/publications/revenue-and-customs-brief-21-2020-withdrawal-of-the-vat-retail-export-scheme-and-the-tax-free-shopping-concession/revenue-and-customs-brief-21-2020-withdrawal-of-the-vat-retail-export-scheme-and-the-tax-free-shopping-concession' }],
  },

  // ── 亞洲其他、大洋洲、中東、非洲 ─ 2026-10-19 逐頁核對。
  Australia: {
    verified: '2026-10-19',
    zh: [
      '同一家商店（同一個 ABN）的發票合計至少 300 澳幣，不同商店的消費不能合併。',
      '發票日期須在離境日前 60 天內，並須在離境前提出退稅申請。',
    ],
    en: [
      'Invoices from one business (one ABN) must total at least A$300; purchases from different businesses cannot be combined.',
      'Invoices must be dated within 60 days before your departure date, and the claim must be lodged before you depart.',
    ],
    sources: [{ label: { zh: '澳洲內政部旅客退稅 (TRS)', en: 'Home Affairs TRS' }, url: 'https://trs.homeaffairs.gov.au/' }],
  },
  UAE: {
    verified: '2026-10-19',
    zh: [
      '須持旅遊簽證入境、年滿 18 歲、不是阿聯酋居民。',
      '每筆退稅交易的合資格消費至少 250 迪拉姆（不含 VAT）。',
      '須在發票日期後 90 天內、於離境時驗證退稅交易，並親自出示所購貨品供檢查。',
      '完成驗證後須在 6 小時內離境，否則退稅會被取消。',
    ],
    en: [
      'You must have entered on a tourist visa, be 18 or older and not a UAE resident.',
      'The minimum eligible spend per VAT refund transaction is AED 250 (excluding VAT).',
      'Validate your tax-free transactions at departure within 90 days of the tax invoice date, and present the goods for inspection in person.',
      'You must depart within 6 hours of completing validation, otherwise the refund is cancelled.',
    ],
    note: { zh: '手續費各官方頁面寫法不一，以退稅營運商 Planet 的現場公告為準。', en: 'Official pages differ on the refund fee; follow the notice from the refund operator Planet.' },
    sources: [{ label: { zh: '阿聯酋聯邦稅務局', en: 'UAE Federal Tax Authority' }, url: 'https://tax.gov.ae/en/services/tourist.vat.refunds.aspx' }],
  },
  'South Africa': {
    verified: '2026-10-19',
    zh: [
      '同一次旅程購買並運出的貨品，含 VAT 合計須超過 250 蘭特（每位合資格買家）。',
      '貨品須在發票日期後 90 天內運出南非。',
      '須帶著貨品，向指定商業港口（如機場）的海關人員出示，海關在發票上背書後才能辦理。',
    ],
    en: [
      'The VAT-inclusive total of goods bought on one visit and exported at the end of it must exceed R250 per qualifying purchaser.',
      'The goods must be exported within 90 days of the tax invoice date.',
      'Present yourself and the goods to a Customs officer at a designated commercial port (such as an airport), who endorses the invoice.',
    ],
    sources: [{ label: 'SARS', url: 'https://www.sars.gov.za/types-of-tax/value-added-tax/vat-refunds-for-tourists-and-foreign-enterprises/' }],
  },
  China: {
    verified: '2026-10-19',
    zh: [
      '同一天在同一家退稅商店消費滿 200 人民幣，即可申請離境退稅（原本是 500 元，2025 年降低）。',
    ],
    en: [
      'Overseas travellers can apply for a departure tax refund after spending at least CNY 200 at the same tax-refund store on the same day (lowered from CNY 500 in 2025).',
    ],
    note: { zh: '其他條件請看官方說明。', en: 'See the official page for the other conditions.' },
    sources: [{ label: { zh: '中國政府網', en: 'Chinese Government' }, url: 'https://english.www.gov.cn/news/202504/27/content_WS680de14bc6d0868f4e8f21bf.html' }],
  },
};

export const TAX_REFUND = T;

// The app hands us country names in several shapes: English keys from the search flow, and the
// reverse-geocoder's own wording in the UI language on the home tab ("日本", "フランス", "대만"…).
// Map every spelling we know to the key above. Anything unknown simply shows no card.
const ALIASES = {
  Japan: ['日本', '일본'],
  'South Korea': ['韓國', '韩国', '南韓', '大韓民國', '韓国', '한국', '대한민국', 'Korea'],
  Taiwan: ['台灣', '臺灣', '台湾', '대만'],
  Thailand: ['泰國', '泰国', 'タイ', '태국'],
  Singapore: ['新加坡', 'シンガポール', '싱가포르'],
  France: ['法國', '法国', 'フランス', '프랑스'],
  Italy: ['義大利', '意大利', '意大利', 'イタリア', '이탈리아'],
  Germany: ['德國', '德国', 'ドイツ', '독일'],
  Switzerland: ['瑞士', 'スイス', '스위스'],
  Austria: ['奧地利', '奥地利', 'オーストリア', '오스트리아'],
  Denmark: ['丹麥', '丹麦', 'デンマーク', '덴마크'],
  Finland: ['芬蘭', '芬兰', 'フィンランド', '핀란드'],
  Hungary: ['匈牙利', 'ハンガリー', '헝가리'],
  Iceland: ['冰島', '冰岛', 'アイスランド', '아이슬란드'],
  Norway: ['挪威', 'ノルウェー', '노르웨이'],
  Portugal: ['葡萄牙', 'ポルトガル', '포르투갈'],
  Spain: ['西班牙', 'スペイン', '스페인'],
  'United Kingdom': ['英國', '英国', 'イギリス', '영국', 'UK', 'Great Britain'],
  Australia: ['澳洲', '澳大利亞', '澳大利亚', 'オーストラリア', '호주'],
  UAE: ['阿聯酋', '阿拉伯聯合酋長國', '阿联酋', '阿拉伯联合酋长国', 'アラブ首長国連邦', '아랍에미리트', 'United Arab Emirates'],
  'South Africa': ['南非', '南非共和國', '南アフリカ', '남아프리카 공화국', '남아프리카'],
  China: ['中國', '中国', '中華人民共和國', '中华人民共和国', '중국'],
};
const NAME_TO_KEY = {};
for (const [key, names] of Object.entries(ALIASES)) {
  NAME_TO_KEY[key.toLowerCase()] = key;
  for (const n of names) NAME_TO_KEY[n.toLowerCase()] = key;
}
// Hong Kong and Macau report "China" as their country but have no consumption tax and are not
// covered by mainland China's departure refund, so a China match is dropped for these cities.
const HK_MACAU_RE = /^(香港|九龍|九龙|新界|澳門|澳门|hong kong|kowloon|new territories|macau|macao|マカオ|홍콩|마카오)$/i;

// Returns the key in TAX_REFUND for a country name (any spelling) / city pair, or null.
// The reverse geocoder can return several spellings joined together ("泰国;泰國"), so try each part.
export const resolveTaxRefundKey = (country, city) => {
  let key = null;
  for (const part of String(country || '').split(/[;；/／]/)) {
    key = NAME_TO_KEY[part.trim().toLowerCase()];
    if (key) break;
  }
  if (!key || !T[key]) return null;
  if (key === 'China' && HK_MACAU_RE.test(String(city || '').trim())) return null;
  return key;
};

// en / ja / ko UI use the English text; every Chinese UI uses the Traditional Chinese text.
export const taxRefundLang = lang => (lang === 'en' || lang === 'ja' || lang === 'ko') ? 'en' : 'zh';
